// -----------------------------------------------------------------------------
// src/components/ChatComponents/EditVideoLinksDialog.js
//   • Handles multiple submission links per creator/video
//   • Robust save-changes flow (explicit disabled state, proper reset)
//   • Mobile-friendly layout preserved
// -----------------------------------------------------------------------------
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
} from "@mui/material";
import CloseIcon        from "@mui/icons-material/Close";
import SaveIcon         from "@mui/icons-material/Save";
import PaidIcon         from "@mui/icons-material/Paid";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import CloudUploadIcon  from "@mui/icons-material/CloudUpload";
import ExpandMoreIcon   from "@mui/icons-material/ExpandMore";
import { useMutation, useQuery } from "react-query";
import API               from "../../API";
import AlertDialog       from "../AlertDialog";
import useAlertDialog    from "../useAlertDialog";
import useIsDesktop      from "../../Hooks/useIsDesktop";
import CreatorTimeline   from "../../CreatorUser/CreatorPages/Dashboard/creatorcomponents/creatorTimeline";
import { TimelineStatus } from "../../Utils/constants";

/* ───────────────────────── helpers ───────────────────────── */
const paymentReady = (vid) =>
  !!vid.liveLink &&
  !!vid.boostCode &&
  vid.crossposts.every((c) => !!c.liveLink && !!c.boostCode) &&
  !vid.payoutComplete;

/* ───────────────────────── Video card ────────────────────── */
function VideoCard({ video, onLinkChange, onCrossLinkChange, canEdit }) {
  const renderCross = (cross) => (
    <Grid key={cross.id} container spacing={1} sx={{ mt: 1, pl: 4, pb: 1 }}>
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          size="small"
          label="Crosspost Live Link"
          value={cross.liveLink || ""}
          onChange={(e) =>
            onCrossLinkChange(
              video.id,
              cross.id,
              e.target.value,
              cross.boostCode
            )
          }
          disabled={!canEdit}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          size="small"
          label="Boost Code"
          value={cross.boostCode || ""}
          onChange={(e) =>
            onCrossLinkChange(
              video.id,
              cross.id,
              cross.liveLink,
              e.target.value
            )
          }
          disabled={!canEdit}
        />
      </Grid>
    </Grid>
  );

  return (
    <Card elevation={2} sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <Chip label={`#${String(video.id).padStart(3, "0")}`} color="primary" size="small" />
          <Chip label={video.promotionPlatform}     color="secondary" size="small" />
          <Chip label={video.promotionType}                            size="small" />
          {video.payoutComplete && <Chip label="Paid" color="success" size="small" />}
        </Stack>

        <Grid container spacing={1}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              label="Live Link"
              value={video.liveLink || ""}
              onChange={(e) =>
                onLinkChange(video.id, e.target.value, video.boostCode)
              }
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Boost Code"
              value={video.boostCode || ""}
              onChange={(e) =>
                onLinkChange(video.id, video.liveLink, e.target.value)
              }
              disabled={!canEdit}
            />
          </Grid>
        </Grid>

        {video.crossposts.map(renderCross)}
      </CardContent>
    </Card>
  );
}

/* ───────────────────────── Dialog ───────────────────────── */
export default function EditVideoLinksDialog({
  campaignId,
  creatorId,
  videoId,
  isOpen,
  onClose,
  isUser,          // true = brand view, false = creator view
  conversationId,
}) {
  /* --------------- state --------------- */
  const [videos, setVideos]             = useState([]);
  const [unsavedChanges, setUnsaved]    = useState(false);
  const [timelineEvents, setTimelineEvents] = useState(undefined);
  const editedEvents = useRef(false);

  const theme      = useTheme();
  const isDesktop  = useIsDesktop();
  const isSmDown   = useMediaQuery(theme.breakpoints.down("sm"));
  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  /* --------------- queries ------------- */
  const {
    data: campaign,
    isLoading: isCampaignLoading,
  } = useQuery(
    ["campaign", campaignId],
    () => API.campaigns.fetch(campaignId),
    { enabled: isOpen && !!campaignId, refetchOnWindowFocus: false }
  );

  const creator = useMemo(
    () => campaign?.creators?.find((c) => c.id === creatorId),
    [campaign, creatorId]
  );

  /* Timeline query */
  const {
    data: timelineData,
    isLoading: isTimelineLoading,
    refetch: refetchTimeline,
  } = useQuery(
    ["timeline", campaignId, creatorId],
    () => API.campaigns.timeline_by_creator(campaignId, creatorId),
    { enabled: isOpen && !!campaignId && !!creatorId, refetchOnWindowFocus: false, retry: false }
  );

  useEffect(() => {
    if (timelineData?.events) {
      setTimelineEvents(
        timelineData.events.map((e) => ({
          ...e,
          projected_date: new Date(e.projected_date),
          ogStatus: e.status,
        }))
      );
    } else if (timelineData === undefined) {
      setTimelineEvents(undefined);
    } else {
      setTimelineEvents([]); // empty list if 404
    }
  }, [timelineData]);

  /* --------------- submission links --------------- */
  const submissionLinks = useMemo(() => {
    if (!creator) return [];
    const set = new Set();
    if (creator.submissionLink) set.add(creator.submissionLink);
    creator.videos?.forEach((v) => v.submissionLink && set.add(v.submissionLink));
    return Array.from(set);
  }, [creator]);

  /* --------------- mutations --------------- */
  const { mutate: updateFields, isLoading: saving } = useMutation(
    (input) => API.creatorConnect.updateCreatorFields(campaignId, input),
    {
      onError: (err) =>
        openDialog("Error", err.response?.data?.error || err.message, closeDialog),
    }
  );

  const { mutate: postAssetsMessage } = useMutation(() =>
    API.conversations.sendMessage(conversationId, {
      message: `I have sent over the draft/assets: ${submissionLinks[0] || "N/A"}`,
    })
  );

  const { mutate: completeVideo, isLoading: paying } = useMutation(
    () =>
      API.campaigns.completeVideo(campaignId, { creator: creatorId, video: videoId }),
    {
      onSuccess: () => {
        openDialog("Paid", "Payout completed.", closeDialog);
        onClose();
      },
      onError: (err) =>
        openDialog("Error", err.response?.data?.error || err.message, closeDialog),
    }
  );

  const { mutate: requestPayment, isLoading: requesting } = useMutation(
    () =>
      API.conversations.sendMessage(conversationId, {
        message: `Creator Requested Payment for Video ${videoId}`,
      }),
    {
      onSuccess: () => {
        openDialog("Sent", "Request sent to brand.", closeDialog);
        onClose();
      },
      onError: (err) =>
        openDialog("Error", err.response?.data?.error || err.message, closeDialog),
    }
  );

  /* Timeline update */
  const { mutate: updateTimeline, isLoading: isUpdatingTimeline } = useMutation(
    ({ input }) => API.creatorConnect.campaignUpdateTimeline(campaignId, creatorId, input),
    {
      onSuccess: () => {
        editedEvents.current = false;
        refetchTimeline();
        openDialog("Saved", "Timeline updated.", closeDialog);
      },
      onError: (e) => openDialog("Error", e.response?.data?.error || e.message, closeDialog),
    }
  );

  /* --------------- effects ---------------- */
  useEffect(() => {
    if (creator && videoId) {
      const parent = creator.videos.find((v) => v.id === videoId);
      if (!parent) return;
      setVideos([
        {
          ...parent,
          crossposts: creator.videos.filter((c) => c.parentId === parent.id),
        },
      ]);
    }
  }, [creator, videoId]);

  /* --------------- helpers ---------------- */
  const canEditLiveLinks =
    campaign?.campaign_status === "Launched" &&
    !["Declined", "Dropped", "Pitched"].includes(creator?.status);

  const liveLinksPayload = () =>
    videos.flatMap((v) => [
      { id: v.id, liveLink: v.liveLink, boostCode: v.boostCode },
      ...v.crossposts.map((c) => ({
        id: c.id,
        liveLink: c.liveLink,
        boostCode: c.boostCode,
      })),
    ]);

  const syncToApi = () =>
    updateFields(
      {
        submissionLink: submissionLinks[0] || null, // API still expects single link
        liveLinks:      liveLinksPayload(),
      },
      {
        onSuccess: () => {
          setUnsaved(false);
          openDialog("Saved", "Fields updated.", closeDialog);
        },
      }
    );

  const handleAssetsSent = () =>
    updateFields(
      { assetsSent: true, submissionLink: submissionLinks[0] || null },
      {
        onSuccess: () => {
          postAssetsMessage();
          openDialog("Done", "Marked as sent.", closeDialog);
        },
      }
    );

  /* Timeline edit helpers */
  const onEditTimelineField = (idx, target, value) => {
    editedEvents.current = true;
    setTimelineEvents((prev) =>
      prev.map((ev, i) => (i === idx ? { ...ev, [target]: value } : ev))
    );
  };

  const onSaveTimeline = () => {
    if (!editedEvents.current || !timelineEvents) return;
    const foundComplete = timelineEvents.some(
      (ev) => ev.ogStatus !== ev.status && ev.status === TimelineStatus[2]
    );
    if (foundComplete) {
      openDialog(
        "Completed Objective",
        "You're setting an objective as complete. This will notify the manager of this campaign. Continue?",
        () => updateTimeline({ input: timelineEvents }),
        null,
        "Continue",
        "Cancel"
      );
    } else {
      updateTimeline({ input: timelineEvents });
    }
  };

  const eligible = videos.length ? paymentReady(videos[0]) : false;
  const loading =
    isCampaignLoading ||
    saving ||
    paying ||
    requesting ||
    isTimelineLoading ||
    isUpdatingTimeline;

  /* --------------- render ---------------- */
  return (
    <>
      <Backdrop open={loading} sx={{ zIndex: (t) => t.zIndex.modal + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
        {/* ─── Title ─── */}
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: theme.palette.primary.contrastText,
            pb: 2,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Edit Video Links</Typography>
            <IconButton size="small" onClick={onClose} sx={{ color: "inherit" }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        {/* ─── Body ─── */}
        <DialogContent sx={{ py: 3 }}>
          {creator && (
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Avatar src={creator.pfphref} alt={creator.name} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>{creator.name}</Typography>
                <Chip
                  label={campaign?.campaign_status}
                  color={campaign?.campaign_status === "Launched" ? "success" : "default"}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Stack>
          )}

          {/* Submission folders (can be multiple) */}
          <Grid container spacing={2} mb={3}>
            {submissionLinks.length === 0 ? (
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "2px dashed grey",
                    borderRadius: 2,
                    textAlign: "center",
                    p: 3,
                    opacity: 0.5,
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="body2">
                    Submission link not set yet — the creator must accept the offer before one is created.
                  </Typography>
                </Box>
              </Grid>
            ) : (
              submissionLinks.map((link, idx) => (
                <Grid item xs={12} sm={submissionLinks.length > 1 ? 6 : 12} key={idx}>
                  <Box
                    sx={{
                      border: "2px dashed grey",
                      borderRadius: 2,
                      textAlign: "center",
                      p: 3,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "grey.50" },
                    }}
                    onClick={() => window.open(link, "_blank")}
                  >
                    <CloudUploadIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="body2" noWrap>
                      {link}
                    </Typography>
                  </Box>
                </Grid>
              ))
            )}
          </Grid>

          {/* Creator-side quick button */}
          {!isUser && (
            <Button
              startIcon={<CloudUploadIcon />}
              onClick={handleAssetsSent}
              disabled={submissionLinks.length === 0}
              fullWidth={isSmDown}
              sx={{ mb: 3 }}
            >
              Upload Assets
            </Button>
          )}

          {/* Live-link cards */}
          {videos.map((v) => (
            <VideoCard
              key={v.id}
              video={v}
              canEdit={canEditLiveLinks}
              onLinkChange={(id, link, code) => {
                setVideos((prev) =>
                  prev.map((vid) =>
                    vid.id === id ? { ...vid, liveLink: link, boostCode: code } : vid
                  )
                );
                setUnsaved(true);
              }}
              onCrossLinkChange={(vidId, cId, link, code) => {
                setVideos((prev) =>
                  prev.map((vid) =>
                    vid.id === vidId
                      ? {
                          ...vid,
                          crossposts: vid.crossposts.map((c) =>
                            c.id === cId ? { ...c, liveLink: link, boostCode: code } : c
                          ),
                        }
                      : vid
                  )
                );
                setUnsaved(true);
              }}
            />
          ))}

          {/* Timeline accordion */}
          <Accordion sx={{ mt: 3 }} disabled={isTimelineLoading}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={700}>Timeline</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {isTimelineLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : timelineEvents === undefined || timelineEvents.length === 0 ? (
                <Typography>No timeline events for this creator yet.</Typography>
              ) : (
                <CreatorTimeline
                  timelineEvents={timelineEvents}
                  onEditField={onEditTimelineField}
                  onSaveChanges={onSaveTimeline}
                  isSavingChanges={isUpdatingTimeline}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </DialogContent>

        {/* ─── Actions ─── */}
        <Divider />
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button onClick={onClose} startIcon={<CloseIcon />} color="error">
            Close
          </Button>

          <Button
            startIcon={<SaveIcon />}
            onClick={syncToApi}
            disabled={!unsavedChanges}
          >
            Save Changes
          </Button>

          {isUser ? (
            <Button
              variant="contained"
              startIcon={<PaidIcon />}
              disabled={!eligible}
              onClick={completeVideo}
              color="secondary"
            >
              Complete Payment
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<RequestQuoteIcon />}
              disabled={!eligible}
              onClick={requestPayment}
              color="secondary"
            >
              Request Payment
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AlertDialog alertState={dialogState} />
    </>
  );
}
